import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import useQuery from 'hooks/useQuery';
import type React from 'react';
import { useGetPublicReportQuery } from 'services/api/public-access';
import type { ReportPeriodType } from 'services/types/report';

import ChartSection from './components/ChartSection';
import ConversationSection from './components/ConversationSection';
import TextSection from './components/TextSection';

const formatDateLong = (date: Date): string => {
  const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  const months = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];
  const dayName = days[date.getUTCDay()];
  const day = date.getUTCDate().toString().padStart(2, '0');
  const monthName = months[date.getUTCMonth()];
  const year = date.getUTCFullYear();
  return `${dayName}, ${day} de ${monthName} de ${year}`;
};

const getPeriodTypeChip = (periodType: ReportPeriodType) => {
  const periodConfig = {
    daily: { label: 'Diario', color: 'default' as const },
    weekly: { label: 'Semanal', color: 'primary' as const },
    monthly: { label: 'Mensual', color: 'secondary' as const },
  };

  const config = periodConfig[periodType];
  return <Chip label={config.label} color={config.color} size="small" variant="outlined" />;
};

const PublicReportView: React.FC = () => {
  const query = useQuery();
  const token = query.get('token');

  const { data: report, isLoading, error } = useGetPublicReportQuery(token || '', { skip: !token });

  if (!token) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: 'grey.100',
        }}
      >
        <Typography variant="h4" color="error" gutterBottom>
          Token no proporcionado
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
          }}
        >
          Por favor verifica el link que recibiste.
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !report) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: 'grey.100',
        }}
      >
        <Typography variant="h4" color="error" gutterBottom>
          Reporte no disponible
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
          }}
        >
          El link puede haber expirado o el reporte ya no está disponible públicamente.
        </Typography>
      </Box>
    );
  }

  const sortedSections = [...(report.sections || [])].sort((a, b) => a.order - b.order);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'grey.50', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1,
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {report.title}
            </Typography>
            {getPeriodTypeChip(report.periodType)}
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {report.businessUnit?.name} • {formatDateLong(new Date(report.beginDate))}
          </Typography>
        </Box>

        {/* Sections grid */}
        {sortedSections.length > 0 && (
          <Grid container spacing={2}>
            {sortedSections.map((section) => (
              <Grid size={{ xs: 12, md: section.width }} key={section.id}>
                {section.sectionType === 'chart' && (
                  <ChartSection section={section} reportId={report.id} isEditMode={false} />
                )}
                {section.sectionType === 'conversation' && (
                  <ConversationSection section={section} reportId={report.id} isEditMode={false} />
                )}
                {section.sectionType === 'text' && (
                  <TextSection section={section} reportId={report.id} isEditMode={false} />
                )}
              </Grid>
            ))}
          </Grid>
        )}

        {/* Footer */}
        <Box sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            Reporte generado por Relif
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PublicReportView;
